import { useEffect, useState } from 'react';

import { Button, Typography, useDeviceType } from '@braskai/ui-kit';

import { Stack } from '@mui/material';

import { useBookACallStore, useLeadSurvey } from '@/entities/leadSurvey';

import { MixpanelEvents } from '@/shared/thirdParty';

import { BANNER_CTA_VARIATIONS, BANNER_GREETINGS } from '../../lib/constants';


const greetingsLine = (rotate: number) => {
  const words = [...BANNER_GREETINGS.slice(rotate), ...BANNER_GREETINGS.slice(0, rotate)];
  const half = `${words.join('   ')}   `;
  return half + half;
};

const GREETINGS_TOP = greetingsLine(0);
const GREETINGS_BOTTOM = greetingsLine(7);

type GreetingsRowProps = {
  duration: string;
  fontSize: number;
  position: 'bottom' | 'top';
  reverse?: boolean;
  text: string;
};

const GreetingsRow = ({ duration, fontSize, position, reverse, text }: GreetingsRowProps) => (
  <Typography
    sx={{
      '@keyframes bookADemoMarquee': {
        from: { transform: 'translateX(0)' },
        to: { transform: 'translateX(-50%)' },
      },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      animation: `bookADemoMarquee ${duration} linear infinite${reverse ? ' reverse' : ''}`,
      color: 'common.white',
      fontSize,
      fontWeight: 500,
      left: 0,
      opacity: 0.14,
      position: 'absolute',
      [position]: 2,
      whiteSpace: 'pre',
      willChange: 'transform',
    }}
  >
    {text}
  </Typography>
);

/**
 * Persistent Book-a-Demo retargeting banner. Full-width, un-dismissable, navy with
 * radius 12 + white pill button (1:1 with ThreeDaysLeftBanner). Shows only for a qualified lead in the
 * `skipped` state and disappears once `booked`. Rotates through 3 CTA variations per mount.
 */
export const BookADemoBanner = () => {
  const { isMobile } = useDeviceType();
  const open = useBookACallStore((state) => state.open);
  const { nextBannerCta, shouldShowBookADemoBanner: isVisible } = useLeadSurvey();

  // Resolve + advance the rotation once on mount (never during render); the hook guards against StrictMode.
  const [ctaIndex, setCtaIndex] = useState<null | number>(null);
  useEffect(() => {
    if (isVisible && ctaIndex === null) {
      const index = nextBannerCta();
      setCtaIndex(index);
      MixpanelEvents.bookADemoBannerShown({ variationIndex: index });
    }
  }, [ctaIndex, isVisible, nextBannerCta]);

  if (!isVisible || ctaIndex === null) {
    return null;
  }

  const variation = BANNER_CTA_VARIATIONS[ctaIndex] ?? BANNER_CTA_VARIATIONS[0];

  const handleClick = () => {
    MixpanelEvents.bookADemoBannerCtaClick({ variationIndex: ctaIndex });
    open();
  };

  return (
    <Stack
      sx={{
        background: 'linear-gradient(100deg, #0A0A24 0%, #0B0B3F 55%, #07077D 100%)',
        borderRadius: 12,
        direction: isMobile ? 'column' : 'row',
        padding: isMobile ? 20 : 24,
        position: 'relative',
        width: '100%',
      }}
    >
      <Stack sx={{ inset: 0, overflow: 'hidden', pointerEvents: 'none', position: 'absolute' }} aria-hidden>
        <GreetingsRow duration="60s" fontSize={isMobile ? 18 : 26} position="top" text={GREETINGS_TOP} />
        <GreetingsRow duration="80s" fontSize={isMobile ? 13 : 19} position="bottom" text={GREETINGS_BOTTOM} reverse />
      </Stack>

      <Stack
        alignItems={isMobile ? 'flex-start' : 'center'}
        direction={isMobile ? 'column' : 'row'}
        gap={16}
        justifyContent="space-between"
        sx={{ position: 'relative', zIndex: 1 }}
        width="100%"
      >
        <Stack gap={4}>
          <Typography color="grey.50" variant={isMobile ? 'body-1-md' : 'title-4-md'}>
            {variation.title}
          </Typography>
          <Typography sx={{ color: '#FFFFFFA6' }} variant={isMobile ? 'caption-1' : 'body-2-md'}>
            {variation.subtitle}
          </Typography>
        </Stack>

        <Button
          data-testid="book-a-demo-banner-cta"
          sx={{ backgroundColor: 'common.white', borderRadius: 10, maxWidth: 200, width: '100%' }}
          variant="outlined"
          onClick={handleClick}
        >
          {variation.cta}
        </Button>
      </Stack>
    </Stack>
  );
};
